import AdvancedSEOHead from "@/components/seo/AdvancedSEOHead";
import { SummaryBlock } from "@/components/summary/SummaryBlock";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Palette, Zap, Target, ArrowRight } from "lucide-react";
import { useCta } from "@/hooks/useCta";
import HeroPlayButton from "@/components/HeroPlayButton";
import FAQAudioBlock from "@/components/FAQAudioBlock";

export default function CreativeStudio() {
  const { handleCtaClick, resolveCta } = useCta();

  const summary = "Notre Creative Studio combine expertise Amazon et créativité pour créer des contenus qui convertissent : A+ Content, Amazon Stores, packaging et stratégie visuelle.";
  
  const bullets = [
    "A+ Content et EBC premium avec guidelines Amazon",
    "Design d'Amazon Stores pour maximiser la conversion",
    "Stratégie visuelle et branding pour marketplaces",
    "Packaging optimisé pour l'écosystème Amazon"
  ];

  return (
    <>
      <AdvancedSEOHead
        title="Creative Studio — Cosmos Wave | A+ Content & Amazon Store Design"
        description="Creative Studio Amazon : A+ Content premium, Amazon Store design, stratégie visuelle et packaging optimisé pour l'écosystème Amazon."
        breadcrumbs={[
          { name: "Creative Studio", url: "/creative-studio" }
        ]}
        keywords={["Creative Studio Amazon", "A+ Content", "Amazon Store design", "packaging Amazon", "branding marketplace"]}
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <SummaryBlock summary={summary} bullets={bullets} className="summary-chip" />

          {/* Hero Section */}
          <section className="text-center py-16">
            <Badge variant="outline" className="mb-6 bg-primary/5 text-primary border-primary/20">
              Creative Studio Amazon
            </Badge>
            <h1 className="text-5xl font-bold tracking-tight text-foreground mb-6">
              Creative Studio
              <span className="block text-primary mt-2">Contenus qui convertissent</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Créativité et expertise Amazon combinées pour des contenus premium : 
              A+ Content, Amazon Stores et stratégie visuelle qui boostent vos conversions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button size="lg" onClick={(e) => handleCtaClick('whatsapp', e)}>
                Discuter du projet
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href={resolveCta('results')}>Voir nos créations</a>
              </Button>
              <HeroPlayButton 
                targetAudioId="audio-ds" 
                label="Digital Shelf : passer à l'offensive" 
              />
            </div>
          </section>

          {/* Services Grid */}
          <section className="py-16">
            <h2 className="text-3xl font-bold text-center mb-12">Nos services créatifs</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: <Palette className="h-8 w-8" />,
                  title: "A+ Content Premium",
                  description: "Création de contenus A+ et EBC qui respectent les guidelines Amazon tout en maximisant l'impact visuel et la conversion."
                },
                {
                  icon: <Target className="h-8 w-8" />,
                  title: "Amazon Store Design",
                  description: "Design de Stores Amazon optimisés pour la navigation, la conversion et l'expérience utilisateur sur tous les appareils."
                },
                {
                  icon: <Zap className="h-8 w-8" />,
                  title: "Stratégie Visuelle",
                  description: "Développement d'une identité visuelle cohérente adaptée aux spécificités des marketplaces Amazon."
                }
              ].map((service, index) => (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="text-primary mb-4">{service.icon}</div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Audio Summary */}
          <FAQAudioBlock
            title="Creative Studio expliqué en 5 minutes"
            audioSrc="/media/faq-methode.mp3"
            subtitle="Découvrez notre approche créative et nos services premium"
            audioId="audio-ds"
            durationHintSec={300}
          />

          {/* CTA Section */}
          <section className="py-16 bg-muted/30 rounded-3xl text-center">
            <h2 className="text-3xl font-bold mb-4">Prêt à sublimer votre présence Amazon ?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Créons ensemble des contenus premium qui se démarquent et convertissent sur Amazon
            </p>
            <Button size="lg" onClick={(e) => handleCtaClick('whatsapp', e)}>
              Démarrer un projet créatif
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </section>
        </div>
      </div>
    </>
  );
}